<div class="designer-page">
  <mat-toolbar color="primary" class="designer-header">
    <button mat-icon-button (click)="back()" aria-label="Back">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <h3 class="designer-title">
      {{ layout?.externalJobReference || 'Job Details' }}
      @if (layout?.jobStatus === 'CLOSED') {
        <span class="closed-badge">(Closed)</span>
      }
    </h3>
    <span class="spacer"></span>
    @if (layout && layout.minimumAttendance && layout.minimumAttendance > 1) {
      <span class="min-attendance-warning">
        <mat-icon fontIcon="fa-exclamation-triangle" fontSet="fas"></mat-icon>
        Minimum {{ layout.minimumAttendance }} Attendees
      </span>
    }
    @if (layout?.externalCaseReference) {
      <span
        class="hazard-case-warning"
        tabindex="0"
        role="note"
        data-testid="hazard-case-warning"
        [matTooltip]="
          'This job is linked to Hazard Case ' + layout.externalCaseReference
        "
        [attr.aria-label]="
          'Warning: this job is linked to Hazard Case ' +
          layout.externalCaseReference
        "
      >
        <mat-icon fontIcon="fa-exclamation-triangle" fontSet="fas"></mat-icon>
        Hazard Case
      </span>
    }
    @if (urmEnabled && layout?.externalJobReference) {
      <urm-core-launch-widget
        [externalJobReference]="layout!.externalJobReference!"
      ></urm-core-launch-widget>
    }
    <button
      mat-flat-button
      class="header-btn"
      color="primary"
      (click)="addVisit()"
      [disabled]="loading || isReadOnly"
    >
      <mat-icon>add</mat-icon>
      Add Visit
    </button>
    <button
      mat-icon-button
      class="header-icon-btn"
      (click)="toggleViewMode()"
      [matTooltip]="
        viewMode === 'horizontal'
          ? 'Switch to vertical (group visits in stacks)'
          : 'Switch to horizontal (one column per visit)'
      "
      aria-label="Toggle visit view mode"
      data-testid="view-mode-toggle"
    >
      <mat-icon>{{
        viewMode === "horizontal" ? "view_week" : "view_agenda"
      }}</mat-icon>
    </button>
    <button
      mat-flat-button
      class="header-btn"
      color="warn"
      (click)="discard()"
      [disabled]="!hasPending() || applying || isReadOnly"
    >
      Discard
    </button>
    <button
      mat-flat-button
      class="header-btn"
      color="accent"
      (click)="apply()"
      [disabled]="!hasPending() || applying || isReadOnly"
      data-testid="apply-button"
    >
      Apply
      @if (hasPending()) {
        <span class="badge">{{ visitOps.length + scheduleOps.length }}</span>
      }
    </button>
  </mat-toolbar>

  @if (loading) {
    <div class="loading">
      <mat-spinner diameter="40"></mat-spinner>
    </div>
  } @else if (!layout) {
    <p>No layout loaded.</p>
  } @else {
    <!-- Bookshelf: left "panels" region (job / requirements / comparison) is always a
         horizontal column accordion. The right "visits" region flips between horizontal
         columns and a vertical accordion based on viewMode. -->
    <div
      class="bookshelf"
      [class.visits-vertical]="viewMode === 'vertical'"
      cdkDropListGroup
      data-testid="bookshelf"
    >
      <div class="panel-region">
        @for (slice of panelSlices(); track slice.key) {
          <ng-container
            *ngTemplateOutlet="sliceTpl; context: { $implicit: slice }"
          ></ng-container>
        }
      </div>
      <div class="visit-region">
        @if (viewMode === "vertical") {
          <!-- Vertical list: numbered summary rows, expand to reveal full visit card -->
          <div class="visit-list" cdkDropListGroup>
            @for (
              visit of visibleVisits;
              track visit.id ?? visit.clientRef;
              let idx = $index
            ) {
              <div
                class="visit-row"
                [ngClass]="'visit-row-status-' + statusClass(visit.visitStatus)"
                [class.visit-row-collapsed]="
                  isSliceCollapsed(
                    visit.id !== null
                      ? 'v-' + visit.id
                      : 'v-new-' + visit.clientRef
                  )
                "
                [attr.data-group-key]="visitGroupKey(visit)"
              >
                <button
                  type="button"
                  class="visit-row-bar"
                  (click)="
                    toggleSlice(
                      visit.id !== null
                        ? 'v-' + visit.id
                        : 'v-new-' + visit.clientRef
                    )
                  "
                  [attr.aria-expanded]="
                    !isSliceCollapsed(
                      visit.id !== null
                        ? 'v-' + visit.id
                        : 'v-new-' + visit.clientRef
                    )
                  "
                >
                  <mat-icon class="visit-row-chev">{{
                    isSliceCollapsed(
                      visit.id !== null
                        ? "v-" + visit.id
                        : "v-new-" + visit.clientRef
                    )
                      ? "expand_more"
                      : "expand_less"
                  }}</mat-icon>
                  <span class="bar-status">{{
                    visitStatusLabel(visit.visitStatus, visit.id === null)
                  }}</span>
                  <span class="bar-visit-type">{{
                    visit.visitTypeDescription ?? "Visit"
                  }}</span>
                  <span
                    class="bar-time"
                    [class.bar-time-unscheduled]="
                      !visitTimeRange(visit).scheduled
                    "
                  >
                    {{ visitTimeRange(visit).label }}
                  </span>
                  <span class="bar-spacer"></span>
                  <span class="bar-duration">
                    <span class="dur-label">
                      {{ scheduleDurationMinutes(visit) }} /
                      {{ plannedDurationTotalMinutes(visit) }} min
                    </span>
                    <span class="dur-track">
                      <span
                        class="dur-fill"
                        [style.width.%]="visitScheduleFraction(visit) * 100"
                      ></span>
                    </span>
                    <span class="dur-reqs">
                      {{ (visit.schedules ?? []).length }}
                      requirement{{
                        (visit.schedules ?? []).length === 1 ? "" : "s"
                      }}
                    </span>
                  </span>
                </button>

                @if (
                  !isSliceCollapsed(
                    visit.id !== null
                      ? "v-" + visit.id
                      : "v-new-" + visit.clientRef
                  )
                ) {
                  <div class="visit-row-body">
                    <ng-container
                      *ngTemplateOutlet="
                        visitCard;
                        context: { $implicit: visit, compact: true }
                      "
                    ></ng-container>
                  </div>
                }
              </div>
            }
          </div>
        } @else {
          @for (slice of visitSlices(); track slice.key) {
            <ng-container
              *ngTemplateOutlet="sliceTpl; context: { $implicit: slice }"
            ></ng-container>
          }
        }
      </div>
    </div>

    <ng-template #sliceTpl let-slice>
      <section
        class="slice"
        [class.slice-collapsed]="isSliceCollapsed(slice.key)"
        [class.slice-panel]="slice.kind === 'panel'"
        [class.slice-group]="slice.kind === 'visit-group'"
        [class.slice-visit]="slice.kind === 'visit'"
        [attr.data-slice-key]="slice.key"
        [attr.data-group-key]="slice.groupKey ?? null"
      >
        <button
          type="button"
          class="slice-spine"
          (click)="toggleSlice(slice.key)"
          [attr.aria-expanded]="!isSliceCollapsed(slice.key)"
          [attr.aria-label]="
            (isSliceCollapsed(slice.key) ? 'Expand ' : 'Collapse ') +
            slice.label
          "
        >
          <mat-icon class="chev">{{
            spineChevron(slice, isSliceCollapsed(slice.key))
          }}</mat-icon>
          <span class="spine-label">{{ slice.label }}</span>
          @if (slice.count !== undefined) {
            <span class="spine-count">{{ slice.count }}</span>
          }
        </button>

        @if (!isSliceCollapsed(slice.key)) {
          <div class="slice-body">
            @switch (slice.key) {
              @case ("job") {
                <ng-container *ngTemplateOutlet="jobBody"></ng-container>
              }
              @case ("requirements") {
                <ng-container
                  *ngTemplateOutlet="requirementsBody"
                ></ng-container>
              }
              @case ("coreComparison") {
                <ng-container *ngTemplateOutlet="coreBody"></ng-container>
              }
              @default {
                @if (slice.kind === "visit-group") {
                  <div class="visit-stack">
                    @for (
                      visit of slice.visits ?? [];
                      track visit.id ?? visit.clientRef
                    ) {
                      <ng-container
                        *ngTemplateOutlet="
                          visitCard;
                          context: { $implicit: visit }
                        "
                      ></ng-container>
                    }
                  </div>
                } @else if (slice.kind === "visit" && slice.visit) {
                  <ng-container
                    *ngTemplateOutlet="
                      visitCard;
                      context: { $implicit: slice.visit }
                    "
                  ></ng-container>
                }
              }
            }
          </div>
        }
      </section>
    </ng-template>
  }
</div>

<!-- ===== templates ===== -->

<ng-template #jobBody>
  @if (urmEnabled && layout?.externalPropertyReference) {
    <div class="caution-banner">
      <mat-icon fontIcon="fa-exclamation-triangle" fontSet="fas"></mat-icon>
      <span class="caution-banner-label">Cautions</span>
    </div>
    <div class="caution-body">
      <urm-caution-widget
        [externalPropertyReference]="layout.externalPropertyReference"
        mode="verbose"
      ></urm-caution-widget>
    </div>
  }

  @if (layout && layout.minimumAttendance && layout.minimumAttendance > 1) {
    <div class="min-attendance-badge">
      <mat-icon fontIcon="fa-users" fontSet="fas"></mat-icon>
      Minimum {{ layout.minimumAttendance }} Attendees Required
    </div>
  }

  @if (layout?.priority?.description) {
    <div
      class="priority-pill"
      [style.background-color]="layout?.priority?.backgroundColor"
      [style.color]="layout?.priority?.foregroundColor"
      data-testid="priority-pill"
    >
      <div class="priority-pill-name">{{ layout?.priority?.description }}</div>
      @if (layout?.planningWindowEnd) {
        <div class="priority-pill-due">
          Due {{ layout?.planningWindowEnd | amDateFormat: "DD/MM/YYYY HH:mm" }}
        </div>
      }
    </div>
  }
  <dl class="job-dl">
    @if (layout?.externalJobReference) {
      <dt>Job Number</dt>
      <dd>{{ layout?.externalJobReference }}</dd>
    }
    <dt>Internal Id</dt>
    <dd>{{ layout?.jobId }}</dd>
    @if (layout?.zoneName) {
      <dt>Zone</dt>
      <dd>{{ layout?.zoneName }}</dd>
    }
    @if (layout?.externalPropertyReference) {
      <dt>Property</dt>
      <dd>{{ layout?.externalPropertyReference }}</dd>
    }
    @if (layout?.externalProjectReference) {
      <dt>Project</dt>
      <dd>{{ layout?.externalProjectReference }}</dd>
    }
    @if (layout?.externalCaseReference) {
      <dt>Case</dt>
      <dd>{{ layout?.externalCaseReference }}</dd>
    }
    @if (layout?.planningWindowEnd) {
      <dt>Plan window end</dt>
      <dd>{{ layout?.planningWindowEnd }}</dd>
    }
    <dt>Visits</dt>
    <dd>{{ visibleVisits.length }}</dd>
  </dl>

  @if (urmEnabled && layout?.externalJobReference) {
    <div class="urm-section">
      <h4 class="urm-heading">Job Description</h4>
      <urm-job-description-widget
        [externalJobReference]="layout.externalJobReference"
        [compact]="false"
      ></urm-job-description-widget>
    </div>
  }

  @if (urmEnabled && layout?.externalPropertyReference) {
    <div class="urm-section">
      <h4 class="urm-heading">Address</h4>
      <urm-property-info
        [externalPropertyReference]="layout.externalPropertyReference"
        [listView]="true"
      ></urm-property-info>
    </div>
  }

  @if (urmEnabled && layout?.externalJobReference) {
    <div class="urm-section">
      <h4 class="urm-heading">Contact Information</h4>
      <urm-job-contact-widget
        [externalJobReference]="layout.externalJobReference"
      ></urm-job-contact-widget>
    </div>
  }
</ng-template>

<ng-template #requirementsBody>
  <div
    class="jr-list"
    cdkDropList
    [id]="jobRequirementsDropListId()"
    [cdkDropListConnectedTo]="jobRequirementsConnectedTo()"
    cdkDropListSortingDisabled
    (cdkDropListDropped)="dropOnJobRequirements($event)"
    data-testid="job-requirements"
  >
    @for (js of layout?.jobSchedules ?? []; track js.id) {
      <div
        class="js-row"
        cdkDrag
        [cdkDragData]="{ kind: 'job-schedule', jobSchedule: js }"
        [cdkDragDisabled]="isReadOnly"
        [class.highlighted]="isHighlighted(js.id)"
        (mouseenter)="hoveredJobScheduleId = js.id"
        (mouseleave)="hoveredJobScheduleId = null"
      >
        <div class="js-row-head">
          @if (js.externalScheduleReference) {
            <span class="line-no">Line {{ js.externalScheduleReference }}</span>
          }
          <span class="sor">{{ js.sorCode }}</span>
        </div>
        <div class="js-row-desc">{{ js.sorDescription }}</div>
        <div class="js-row-stats">
          <span><label>SMV</label>{{ js.sorSmv }}</span>
          <span class="primary"
            ><label>Required</label>{{ js.allowedQuantity }}</span
          >
          <span
            class="remaining primary"
            [class.over]="js.allowedQuantity - js.allocatedQuantity < 0"
          >
            <label>Outstanding</label
            >{{ js.allowedQuantity - js.allocatedQuantity }}
          </span>
          @if (js.archivedQuantity > 0) {
            <span class="archived" matTooltip="Archived from earlier visits">
              <label>Archived</label>{{ js.archivedQuantity }} of
              {{ js.allowedQuantity + js.archivedQuantity }}
            </span>
          }
        </div>
        @if (js.sorVisitTypeDescription) {
          <div class="js-row-visit-type">{{ js.sorVisitTypeDescription }}</div>
        }
      </div>
    }
  </div>
</ng-template>

<ng-template #coreBody>
  @if (!comparison?.hasChanges) {
    <p class="muted">Resolve matches Core.</p>
  } @else {
    <table class="core-table">
      <thead>
        <tr>
          <th>Status</th>
          <th>SOR</th>
          <th class="num">Resolve</th>
          <th class="num">Core</th>
        </tr>
      </thead>
      <tbody>
        @for (line of comparison?.lines; track $index) {
          <tr [class]="'kind-' + line.kind.toLowerCase()">
            <td>{{ line.kind }}</td>
            <td>{{ line.sorCode }}</td>
            <td class="num">{{ line.resolveQuantity }}</td>
            <td class="num">{{ line.coreQuantity }}</td>
          </tr>
        }
      </tbody>
    </table>
  }
</ng-template>

<ng-template #visitCard let-visit let-compact="compact">
  <div
    class="lane"
    [class.lane-locked]="visit.locked"
    [class.lane-cancelled]="visit.visitStatus === 'CANCELLED'"
    [class.lane-completed]="visit.visitStatus === 'COMPLETED'"
    [class.lane-followon-pending]="needsFollowOn(visit)"
    [class.lane-compact]="compact"
  >
    @if (!compact) {
      <header class="lane-header">
        <div class="lane-title-block">
          @if (visit.visitTypeDescription) {
            <span class="lane-visit-type">{{
              visit.visitTypeDescription
            }}</span>
          } @else {
            <span class="lane-visit-type lane-visit-type-missing">Visit</span>
          }
          <span class="lane-subline">
            @if (visit.id !== null) {
              <span class="lane-id">#{{ visit.id }}</span>
            } @else {
              <em class="lane-id">(draft {{ visit.clientRef }})</em>
            }
          </span>
        </div>
        <span class="spacer"></span>
        @if (visit.id === null) {
          <button
            mat-icon-button
            aria-label="Discard staged visit"
            matTooltip="Discard this staged visit"
            (click)="removeStagedVisit(visit)"
          >
            <mat-icon>close</mat-icon>
          </button>
        }
      </header>

      @if (isProject) {
        <div class="project-notice">
          <mat-icon fontIcon="fa-exclamation-circle" fontSet="fas"></mat-icon>
          Project Visit — Rebook via Orchestrator
        </div>
      }

      @if (visit.id !== null) {
        <app-visit-booking-button
          class="lane-booking-button"
          [visitId]="visit.id"
          [visitStatus]="visit.visitStatus"
          [appointmentStart]="visit.appointmentStart"
          [appointmentEnd]="visit.appointmentEnd"
          [disabled]="hasPending()"
          [orchestrated]="isProject"
          [matTooltip]="hasPending() ? 'Apply or discard changes first' : ''"
          (actionCompleted)="refresh()"
        ></app-visit-booking-button>
      }
    }

    <div class="lane-content" [class.lane-content-grid]="compact">
      @if (compact && visit.id !== null) {
        <div class="grid-booking-col">
          <app-visit-booking-button
            [visitId]="visit.id"
            [visitStatus]="visit.visitStatus"
            [appointmentStart]="visit.appointmentStart"
            [appointmentEnd]="visit.appointmentEnd"
            [disabled]="hasPending()"
            [orchestrated]="isProject"
            [matTooltip]="hasPending() ? 'Apply or discard changes first' : ''"
            (actionCompleted)="refresh()"
          ></app-visit-booking-button>
          @if (
            scheduleDurationMinutes(visit) > 0 ||
            plannedDurationLabel(visit) ||
            actualDurationLabel(visit)
          ) {
            <dl class="duration-summary" data-testid="duration-summary">
              @if (scheduleDurationMinutes(visit) > 0) {
                <div class="duration-row">
                  <dt>Schedule</dt>
                  <dd>{{ scheduleDurationMinutes(visit) }}&thinsp;min</dd>
                </div>
              }
              @if (plannedDurationLabel(visit)) {
                <div class="duration-row">
                  <dt>Planned</dt>
                  <dd>{{ plannedDurationLabel(visit) }}</dd>
                </div>
              }
              @if (actualDurationLabel(visit)) {
                <div class="duration-row">
                  <dt>Actual</dt>
                  <dd>{{ actualDurationLabel(visit) }}</dd>
                </div>
              }
            </dl>
          }
        </div>
      }

      <div class="grid-tickets-col">
        @if (ticketsOf(visit).length > 0) {
          <div class="ticket-cards" data-testid="ticket-cards">
            @for (t of ticketsOf(visit); track t.id ?? $index) {
              <button
                type="button"
                class="ticket-card"
                [ngClass]="'status-' + statusClass(t.ticketStatus)"
                [style.border-left-color]="
                  ticketColour(t.ticketStatus)?.background
                "
                [matTooltip]="t.completionDescription ?? ''"
                (click)="openTicket(t)"
                data-testid="ticket-card"
              >
                <div class="ticket-card-head">
                  <span class="ticket-status">{{ t.ticketStatus }}</span>
                  @if (t.completionCode) {
                    <span class="ticket-code">{{ t.completionCode }}</span>
                  }
                </div>
                @if (t.resourceName) {
                  <div class="ticket-assignee">
                    @if (t.resourceImageUrl) {
                      <img
                        [src]="t.resourceImageUrl"
                        [alt]="t.resourceName"
                        class="assignee-avatar"
                      />
                    } @else {
                      <span class="assignee-initials">{{
                        getInitials(t.resourceName)
                      }}</span>
                    }
                    <span class="assignee-name">{{ t.resourceName }}</span>
                  </div>
                }
                @if (t.skillProfileDescription) {
                  <div class="ticket-skill">
                    {{ t.skillProfileDescription }}
                  </div>
                }
                <div class="ticket-card-footer">
                  <div class="ticket-card-flags">
                    @if (t.followOnRequired && !t.followOnActioned) {
                      <mat-icon
                        class="follow-on-flag"
                        matTooltip="Follow-on required"
                        >flag</mat-icon
                      >
                    }
                    @if (t.referred) {
                      <mat-icon class="referred-flag" matTooltip="Referred"
                        >alt_route</mat-icon
                      >
                    }
                    @if (t.id !== null) {
                      <span class="ticket-id">#{{ t.id }}</span>
                    }
                  </div>
                  @if (t.durationMinutes) {
                    <span class="ticket-duration"
                      >{{ t.durationMinutes }}&thinsp;min</span
                    >
                  }
                </div>
              </button>
            }
            @if (ticketsOf(visit).length === 1 && canAmend(visit)) {
              <button
                type="button"
                class="ticket-card ticket-card-add-coworker"
                [matTooltip]="hasPending() ? 'Apply or discard changes first' : 'Add a second resource requirement (opens Amend Resource Requirements)'"
                [disabled]="hasPending()"
                (click)="amendRequirements(visit)"
                data-testid="add-coworker"
              >
                <mat-icon>person_add</mat-icon>
                <span>Add coworker</span>
              </button>
            } @else if (ticketsOf(visit).length === 1) {
              <div
                class="ticket-card ticket-card-empty"
                aria-hidden="true"
              ></div>
            }
          </div>
        }

        <div class="amend-row">
          @if (canAmend(visit)) {
            <button
              mat-stroked-button
              color="primary"
              class="amend-req-button"
              [disabled]="hasPending()"
              [matTooltip]="hasPending() ? 'Apply or discard changes first' : ''"
              (click)="amendRequirements(visit)"
              data-testid="amend-requirements"
            >
              Amend Resource Requirements
            </button>
          } @else {
            <button
              mat-stroked-button
              disabled
              class="amend-req-button"
              [matTooltip]="amendRestrictionReason(visit)"
            >
              Amend Resource Requirements
            </button>
          }
        </div>

        @if (needsFollowOn(visit)) {
          <div class="follow-on-bar">
            <mat-icon>flag</mat-icon>
            <span class="follow-on-text"
              >Follow-on work required by ticket outcome.</span
            >
            <button
              mat-stroked-button
              color="primary"
              (click)="createFollowOn(visit)"
              data-testid="create-follow-on"
            >
              <mat-icon>add</mat-icon>
              Create follow-on
            </button>
          </div>
        }
      </div>

      @if (!compact) {
        @if (
          scheduleDurationMinutes(visit) > 0 ||
          plannedDurationLabel(visit) ||
          actualDurationLabel(visit)
        ) {
          <dl class="duration-summary" data-testid="duration-summary">
            @if (scheduleDurationMinutes(visit) > 0) {
              <div class="duration-row">
                <dt>Schedule</dt>
                <dd>{{ scheduleDurationMinutes(visit) }}&thinsp;min</dd>
              </div>
            }
            @if (plannedDurationLabel(visit)) {
              <div class="duration-row">
                <dt>Planned</dt>
                <dd>{{ plannedDurationLabel(visit) }}</dd>
              </div>
            }
            @if (actualDurationLabel(visit)) {
              <div class="duration-row">
                <dt>Actual</dt>
                <dd>{{ actualDurationLabel(visit) }}</dd>
              </div>
            }
          </dl>
        }
      }

      <div
        cdkDropList
        [cdkDropListData]="visit"
        [cdkDropListConnectedTo]="laneDropListIds()"
        [id]="dropListId(visit)"
        (cdkDropListDropped)="drop($event)"
        class="schedule-list"
      >
        @if (visibleSchedules(visit).length === 0) {
          <p class="empty">No schedules</p>
        }
        @for (sch of visibleSchedules(visit); track sch.id ?? $index) {
          <div
            cdkDrag
            [cdkDragData]="{ schedule: sch, sourceVisit: visit }"
            [cdkDragDisabled]="
              sch.archivedGhost ||
              (sch.quantity ?? 0) <= 0 ||
              !canDragFrom(visit)
            "
            [matTooltip]="canDragFrom(visit) ? '' : dragBlockedReason(visit)"
            class="schedule-row"
            [class.ghost]="sch.archivedGhost"
            [class.undraggable]="!canDragFrom(visit)"
            [class.highlighted]="isHighlighted(sch.jobScheduleId)"
            (mouseenter)="hoveredJobScheduleId = sch.jobScheduleId"
            (mouseleave)="hoveredJobScheduleId = null"
          >
            <div class="js-row-head">
              @if (sch.externalScheduleReference) {
                <span class="line-no"
                  >Line {{ sch.externalScheduleReference }}</span
                >
              }
              <span class="sor">{{ sch.sorCode }}</span>
            </div>
            <div class="js-row-desc">{{ sch.sorDescription }}</div>
            <div class="row-qty-line">
              @if (sch.archivedGhost) {
                <em class="archived-label">archived {{ sch.archivedQuantity }}</em>
              } @else {
                <span class="qty">{{ sch.quantity }}</span>
              }
            </div>
          </div>
        }
      </div>
    </div>
  </div>
</ng-template>
